import styles from "./ModifySuperUser.module.css";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FormControlLabel, Switch, TextField } from "@mui/material";
import api_endpoints from "../../api";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import ButtonLoading from "../../widgets/ButtonLoading/ButtonLoading";
import CancelButton from "../../widgets/CancelButton/CancelButton";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import { getPageFromName } from "../../route_utils";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import AuthContext from "../../context/AuthContext/AuthContext";

const ModifySuperUser = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id } = useParams();
  const { t } = useCustomTranslation("Super utilisateurs");
  const authContext = useContext(AuthContext);
  const superUserId = state?.super_user_id || id;
  const currentUserId = authContext?.authContext?.accessToken?.id;
  const isCurrentUser =
    Boolean(superUserId && currentUserId) &&
    String(superUserId) === String(currentUserId);

  const { setShowSuccessAlert, setMessageSuccessAlert } = useSuccessAlert();

  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    is_active: true,
    is_owner: false,
  });

  useEffect(() => {
    if (!superUserId) {
      setLoading(false);
      setLoadingError(true);
      return;
    }

    fetchSuperUser();
  }, [superUserId]);

  const fetchSuperUser = async () => {
    setLoading(true);
    setLoadingError(false);

    try {
      const response = await api_endpoints.getSuperUserById(superUserId);

      if (response.status === 200) {
        const superUser = response.data;

        setFormData({
          first_name: superUser.first_name || "",
          last_name: superUser.last_name || "",
          email: superUser.email || "",
          is_active: Boolean(superUser.is_active),
          is_owner: Boolean(superUser.is_owner),
        });
      } else {
        setLoadingError(true);
      }
    } catch (error) {
      console.error(error);
      setLoadingError(true);
    } finally {
      setLoading(false);
    }
  };

  const changeFormData = (key, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const response = await api_endpoints.updateSuperUser(superUserId, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        is_active: formData.is_active,
      });

      if (response.status === 200) {
        setMessageSuccessAlert(t("superuser_update_success"));
        setShowSuccessAlert(true);

        navigate(
          getPageFromName("Voir le super utilisateur").path.replace(":id", superUserId),
          {
            state: { super_user_id: superUserId },
          }
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <LoadingContainer loading={loading} error={loadingError}>
      <div className={styles.main_container}>
        <form id="modify_super_user_form" onSubmit={handleSubmit}>
          <div className={styles.user_details_container}>
            <div className="text-lg font-semibold">{t("superuser_edit_title")}</div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <TextField
                fullWidth
                required
                label={t("superuser_table_firstname")}
                value={formData.first_name}
                onChange={(e) => changeFormData("first_name", e.target.value)}
              />

              <TextField
                fullWidth
                required
                label={t("superuser_table_lastname")}
                value={formData.last_name}
                onChange={(e) => changeFormData("last_name", e.target.value)}
              />

              <TextField
                fullWidth
                required
                label={t("superuser_table_email")}
                value={formData.email}
                onChange={(e) => changeFormData("email", e.target.value)}
              />

              <div className="flex items-center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      disabled={formData.is_owner || isCurrentUser}
                      onChange={(e) => changeFormData("is_active", e.target.checked)}
                    />
                  }
                  label={t("superuser_edit_active_label")}
                />
              </div>
            </div>
          </div>
        </form>

        <div className="flex justify-end space-x-4 mt-4">
          <CancelButton disabled={saving} label={t("superuser_delete_dialog_cancel")} />
          <ButtonLoading
            variant="contained"
            type="submit"
            form="modify_super_user_form"
            loading={saving}
          >
            {t("superuser_edit_save")}
          </ButtonLoading>
        </div>
      </div>
    </LoadingContainer>
  );
};

export default ModifySuperUser;
